import { ref } from './a1'
import { blankSheet } from './store'
import type { Cell, CellStyle, Sheet } from './types'

export interface SheetTemplate {
  id: string
  name: string
  category: string
  description: string
  accent: string
  icon: string
  create: () => Sheet
}

const thin = (color = '#d7dee8') => ({ style: 'thin' as const, color })
const bottom = (color = '#94a3b8') => ({ style: 'medium' as const, color })
const titleStyle = (bg: string): CellStyle => ({
  bg,
  color: '#ffffff',
  bold: true,
  fontSize: 20,
  verticalAlign: 'middle',
  align: 'left',
})
const sectionStyle = (bg: string, color = '#ffffff'): CellStyle => ({
  bg,
  color,
  bold: true,
  fontSize: 12,
  verticalAlign: 'middle',
  borderBottom: bottom(bg),
})
const headerStyle = (bg: string): CellStyle => ({
  bg,
  color: '#ffffff',
  bold: true,
  align: 'center',
  verticalAlign: 'middle',
  wrap: true,
  borderTop: thin(bg),
  borderRight: thin('#ffffff'),
  borderBottom: bottom(bg),
  borderLeft: thin(bg),
})
const bodyStyle = (bg = '#ffffff'): CellStyle => ({
  bg,
  verticalAlign: 'middle',
  borderBottom: thin(),
})
const inputStyle: CellStyle = { ...bodyStyle('#fffdf2'), color: '#334155' }
const formulaStyle: CellStyle = { ...bodyStyle('#eef6ff'), color: '#0f3d66', bold: true }

function put(sheet: Sheet, address: string, value: string | number, style?: CellStyle, formula = false) {
  const cell: Cell = formula ? { f: String(value), s: style } : { v: value, s: style }
  sheet.cells[address] = cell
}

function fillRow(sheet: Sheet, row: number, values: Array<string | number>, style: CellStyle) {
  values.forEach((value, i) => put(sheet, ref(row, i + 1), value, style))
}

function makeBudget(): Sheet {
  const s = blankSheet('월간 예산', 80, 8)
  s.merges = [{ r1: 1, c1: 1, r2: 1, c2: 8 }]
  s.rowHeights = { 1: 42, 3: 28, 7: 30 }
  s.colWidths = { 1: 150, 2: 115, 3: 115, 4: 115, 5: 115, 6: 120, 7: 120, 8: 180 }
  put(s, 'A1', '월간 예산 플래너', titleStyle('#2563eb'))
  put(s, 'A2', '입력 셀', { color: '#64748b', italic: true })
  put(s, 'B2', '연한 노랑', { ...inputStyle, align: 'center' })
  fillRow(s, 3, ['기준 월', '예산 합계', '지출 합계', '잔액', '저축률', '', '', ''], sectionStyle('#dbeafe', '#1e3a8a'))
  put(s, 'A4', '2026-07', inputStyle)
  put(s, 'B4', '=SUM(C8:C19)', { ...formulaStyle, numFmt: '₩#,##0' }, true)
  put(s, 'C4', '=SUM(D8:D19)', { ...formulaStyle, numFmt: '₩#,##0' }, true)
  put(s, 'D4', '=B4-C4', { ...formulaStyle, numFmt: '₩#,##0' }, true)
  put(s, 'E4', '=IF(B4=0,0,D4/B4)', { ...formulaStyle, numFmt: '0.0%' }, true)
  fillRow(s, 7, ['분류', '항목', '월 예산', '실제 지출', '차이', '결제일', '상태', '메모'], headerStyle('#2563eb'))
  const rows = [
    ['주거', '월세/관리비', 900000],
    ['생활', '식비', 450000],
    ['생활', '교통비', 120000],
    ['통신', '통신비', 80000],
    ['건강', '보험/의료', 150000],
    ['여가', '문화/취미', 180000],
    ['저축', '저축/투자', 500000],
    ['기타', '기타 지출', 100000],
  ]
  rows.forEach((row, i) => {
    const n = i + 8
    const bg = i % 2 ? '#f8fafc' : '#ffffff'
    put(s, `A${n}`, row[0], bodyStyle(bg))
    put(s, `B${n}`, row[1], bodyStyle(bg))
    put(s, `C${n}`, row[2], { ...inputStyle, bg, numFmt: '₩#,##0' })
    put(s, `D${n}`, 0, { ...inputStyle, bg, numFmt: '₩#,##0' })
    put(s, `E${n}`, `=C${n}-D${n}`, { ...formulaStyle, bg, numFmt: '₩#,##0' }, true)
    put(s, `F${n}`, '', { ...inputStyle, bg, numFmt: 'yyyy-mm-dd', align: 'center' })
    put(s, `G${n}`, `=IF(D${n}=0,"예정",IF(E${n}<0,"초과","정상"))`, { ...formulaStyle, bg, align: 'center' }, true)
    put(s, `H${n}`, '', { ...inputStyle, bg, wrap: true })
  })
  return s
}

function makeProject(): Sheet {
  const s = blankSheet('프로젝트 트래커', 100, 9)
  s.merges = [{ r1: 1, c1: 1, r2: 1, c2: 9 }]
  s.rowHeights = { 1: 42, 3: 30, 6: 30 }
  s.colWidths = { 1: 70, 2: 220, 3: 110, 4: 115, 5: 115, 6: 90, 7: 110, 8: 110, 9: 220 }
  put(s, 'A1', '프로젝트 진행 현황', titleStyle('#7c3aed'))
  fillRow(s, 3, ['전체 작업', '완료', '진행 중', '지연', '전체 진행률', '', '', '', ''], sectionStyle('#ede9fe', '#4c1d95'))
  put(s, 'A4', '=COUNTA(B7:B18)', formulaStyle, true)
  put(s, 'B4', '=COUNTIF(G7:G18,"완료")', formulaStyle, true)
  put(s, 'C4', '=COUNTIF(G7:G18,"진행 중")', formulaStyle, true)
  put(s, 'D4', '=COUNTIF(G7:G18,"지연")', formulaStyle, true)
  put(s, 'E4', '=IFERROR(AVERAGE(F7:F18),0)', { ...formulaStyle, numFmt: '0%' }, true)
  fillRow(s, 6, ['ID', '업무명', '담당자', '시작일', '마감일', '진행률', '상태', '우선순위', '메모'], headerStyle('#7c3aed'))
  const tasks = [
    ['P-001', '요구사항 정리', '김담당', '2026-07-01', '2026-07-03', 1, '완료', '높음'],
    ['P-002', '화면 설계', '이기획', '2026-07-03', '2026-07-08', 0.75, '진행 중', '높음'],
    ['P-003', '개발', '박개발', '2026-07-08', '2026-07-20', 0.35, '진행 중', '높음'],
    ['P-004', '검수', '최검수', '2026-07-21', '2026-07-24', 0, '예정', '보통'],
    ['P-005', '배포', '박개발', '2026-07-25', '2026-07-25', 0, '예정', '보통'],
  ]
  tasks.forEach((row, i) => {
    const n = i + 7
    row.forEach((v, c) => put(s, ref(n, c + 1), v, {
      ...(c === 5 ? formulaStyle : inputStyle),
      bg: i % 2 ? '#fafafa' : '#ffffff',
      ...(c === 3 || c === 4 ? { numFmt: 'yyyy-mm-dd', align: 'center' as const } : {}),
      ...(c === 5 ? { numFmt: '0%', align: 'center' as const } : {}),
      ...(c === 0 || c === 6 || c === 7 ? { align: 'center' as const } : {}),
    }))
    put(s, `I${n}`, '', { ...inputStyle, bg: i % 2 ? '#fafafa' : '#ffffff', wrap: true })
  })
  return s
}

function makeSales(): Sheet {
  const s = blankSheet('매출 대시보드', 100, 8)
  s.merges = [{ r1: 1, c1: 1, r2: 1, c2: 8 }]
  s.rowHeights = { 1: 42, 3: 30, 7: 30 }
  s.colWidths = { 1: 120, 2: 150, 3: 100, 4: 120, 5: 120, 6: 130, 7: 120, 8: 180 }
  put(s, 'A1', '매출 성과 대시보드', titleStyle('#0f766e'))
  fillRow(s, 3, ['총 매출', '총 주문', '평균 주문액', '목표 달성률', '최고 매출 채널', '', '', ''], sectionStyle('#ccfbf1', '#134e4a'))
  put(s, 'A4', '=SUM(F8:F19)', { ...formulaStyle, numFmt: '₩#,##0' }, true)
  put(s, 'B4', '=SUM(E8:E19)', { ...formulaStyle, numFmt: '#,##0' }, true)
  put(s, 'C4', '=IFERROR(A4/B4,0)', { ...formulaStyle, numFmt: '₩#,##0' }, true)
  put(s, 'D4', '=IFERROR(A4/SUM(G8:G19),0)', { ...formulaStyle, numFmt: '0.0%' }, true)
  put(s, 'E4', '온라인', formulaStyle)
  fillRow(s, 7, ['날짜', '상품', '채널', '담당', '주문 수', '매출', '목표', '비고'], headerStyle('#0f766e'))
  const rows = [
    ['2026-07-01', '베이직', '온라인', '김영업', 18, 1350000, 1200000],
    ['2026-07-02', '프로', '파트너', '이영업', 9, 1800000, 1500000],
    ['2026-07-03', '베이직', '오프라인', '박영업', 14, 1050000, 1100000],
    ['2026-07-04', '엔터프라이즈', '온라인', '김영업', 3, 2700000, 2500000],
  ]
  rows.forEach((row, i) => {
    const n = i + 8
    row.forEach((v, c) => put(s, ref(n, c + 1), v, {
      ...inputStyle,
      bg: i % 2 ? '#f8fafc' : '#ffffff',
      ...(c === 0 ? { numFmt: 'yyyy-mm-dd', align: 'center' as const } : {}),
      ...(c === 4 ? { numFmt: '#,##0', align: 'right' as const } : {}),
      ...(c === 5 || c === 6 ? { numFmt: '₩#,##0', align: 'right' as const } : {}),
    }))
    put(s, `H${n}`, '', { ...inputStyle, bg: i % 2 ? '#f8fafc' : '#ffffff' })
  })
  return s
}

function makeInventory(): Sheet {
  const s = blankSheet('재고 관리', 120, 8)
  s.merges = [{ r1: 1, c1: 1, r2: 1, c2: 8 }]
  s.rowHeights = { 1: 42, 3: 30, 6: 30 }
  s.colWidths = { 1: 110, 2: 190, 3: 110, 4: 90, 5: 90, 6: 120, 7: 120, 8: 200 }
  put(s, 'A1', '재고 관리 현황', titleStyle('#c2410c'))
  fillRow(s, 3, ['품목 수', '재고 수량', '재고 가치', '부족 품목', '', '', '', ''], sectionStyle('#ffedd5', '#7c2d12'))
  put(s, 'A4', '=COUNTA(B7:B30)', formulaStyle, true)
  put(s, 'B4', '=SUM(D7:D30)', { ...formulaStyle, numFmt: '#,##0' }, true)
  put(s, 'C4', '=SUMPRODUCT(D7:D30,F7:F30)', { ...formulaStyle, numFmt: '₩#,##0' }, true)
  put(s, 'D4', '=COUNTIF(G7:G30,"발주 필요")', formulaStyle, true)
  fillRow(s, 6, ['SKU', '품목명', '분류', '현재고', '안전재고', '단가', '상태', '메모'], headerStyle('#c2410c'))
  const rows = [
    ['SKU-001', '무선 키보드', '주변기기', 34, 10, 39000],
    ['SKU-002', '무선 마우스', '주변기기', 8, 12, 25000],
    ['SKU-003', 'USB-C 허브', '액세서리', 21, 8, 49000],
    ['SKU-004', '노트북 스탠드', '액세서리', 6, 10, 32000],
  ]
  rows.forEach((row, i) => {
    const n = i + 7
    row.forEach((v, c) => put(s, ref(n, c + 1), v, {
      ...inputStyle,
      bg: i % 2 ? '#fffaf5' : '#ffffff',
      ...(c === 3 || c === 4 ? { numFmt: '#,##0', align: 'right' as const } : {}),
      ...(c === 5 ? { numFmt: '₩#,##0', align: 'right' as const } : {}),
    }))
    put(s, `G${n}`, `=IF(D${n}<=E${n},"발주 필요","정상")`, { ...formulaStyle, bg: i % 2 ? '#fffaf5' : '#ffffff', align: 'center' }, true)
    put(s, `H${n}`, '', { ...inputStyle, bg: i % 2 ? '#fffaf5' : '#ffffff', wrap: true })
  })
  return s
}

function makeWeekly(): Sheet {
  const s = blankSheet('주간 업무 계획', 80, 8)
  s.merges = [{ r1: 1, c1: 1, r2: 1, c2: 8 }]
  s.rowHeights = { 1: 42, 3: 30, 6: 30 }
  s.colWidths = { 1: 85, 2: 220, 3: 105, 4: 105, 5: 105, 6: 110, 7: 110, 8: 220 }
  put(s, 'A1', '주간 업무 계획', titleStyle('#334155'))
  fillRow(s, 3, ['주간 목표', '완료 업무', '진행 업무', '완료율', '', '', '', ''], sectionStyle('#e2e8f0', '#0f172a'))
  put(s, 'A4', '핵심 목표를 입력하세요', inputStyle)
  put(s, 'B4', '=COUNTIF(F7:F30,"완료")', formulaStyle, true)
  put(s, 'C4', '=COUNTIF(F7:F30,"진행 중")', formulaStyle, true)
  put(s, 'D4', '=IFERROR(B4/COUNTA(B7:B30),0)', { ...formulaStyle, numFmt: '0%' }, true)
  fillRow(s, 6, ['요일', '업무', '담당', '시작', '마감', '상태', '우선순위', '메모'], headerStyle('#334155'))
  ;['월', '화', '수', '목', '금'].forEach((day, i) => {
    const n = i + 7
    put(s, `A${n}`, day, { ...bodyStyle(i % 2 ? '#f8fafc' : '#ffffff'), bold: true, align: 'center' })
    ;['B', 'C', 'D', 'E', 'F', 'G', 'H'].forEach((col) =>
      put(s, `${col}${n}`, '', { ...inputStyle, bg: i % 2 ? '#f8fafc' : '#ffffff', wrap: col === 'B' || col === 'H' }))
  })
  return s
}

export const SHEET_TEMPLATES: SheetTemplate[] = [
  { id: 'monthly-budget', name: '월간 예산', category: '재무', description: '예산·실제 지출·잔액과 저축률을 한눈에 관리합니다.', accent: '#2563eb', icon: '💳', create: makeBudget },
  { id: 'project-tracker', name: '프로젝트 트래커', category: '프로젝트', description: '업무별 담당자, 일정, 진행률과 상태를 추적합니다.', accent: '#7c3aed', icon: '📌', create: makeProject },
  { id: 'sales-dashboard', name: '매출 대시보드', category: '영업', description: '주문·매출·목표 달성률을 요약하고 원본 데이터를 기록합니다.', accent: '#0f766e', icon: '📈', create: makeSales },
  { id: 'inventory', name: '재고 관리', category: '운영', description: '현재고와 안전재고를 비교해 발주 필요 품목을 표시합니다.', accent: '#c2410c', icon: '📦', create: makeInventory },
  { id: 'weekly-plan', name: '주간 업무 계획', category: '업무', description: '요일별 업무와 우선순위, 완료율을 관리합니다.', accent: '#334155', icon: '🗓️', create: makeWeekly },
]
